import React from 'react';
import styled from 'styled-components';

const AboutContainer = styled.section`
  id: about;
  padding: ${({ theme }) => `${theme.spacing.content.section} ${theme.spacing.content.base}`};
  background: ${({ theme }) => theme.colors.background.primary};
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing['3xl']} ${theme.spacing.content.narrow}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing['2xl']} ${theme.spacing.content.narrow}`};
  }
`;

const AboutContent = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.wide};
  margin: 0 auto;
  text-align: center;
`;

const AboutTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }
`;

const AboutSubtitle = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing['3xl']} 0;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const ExpertiseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing['3xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
  }
`;

const ExpertiseCard = styled.div`
  background: ${({ theme, variant }) => {
    if (variant === 'systems-engineering') {
      return `
        linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.6) 100%),
        url('/images/about/systems-engineering-heritage.webp')`;
    }
    return theme.colors.background.secondary;
  }};
  background-size: cover;
  background-position: center;
  color: ${({ theme, variant }) => 
    variant === 'systems-engineering' ? theme.colors.text.inverse : 'inherit'};
  padding: ${({ theme }) => theme.spacing['2xl']};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  transition: all ${({ theme }) => theme.transitions.base};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  text-align: left;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.cardHover};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

const CardIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 50px;
    height: 50px;
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
`;

const RoyalNavyLogo = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  margin: 0 auto ${({ theme }) => theme.spacing.lg} auto;
  display: block;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 70px;
    height: 70px;
  }
`;

const CardTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: inherit;
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }
`;

const CardDescription = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: inherit;
  opacity: 0.9;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const KeyAchievements = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Achievement = styled.li`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: inherit;
  opacity: 0.95;
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  padding-left: ${({ theme }) => theme.spacing.lg};
  position: relative;

  &:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: ${({ theme, $variant }) => 
      $variant === 'systems-engineering' ? theme.colors.status.successBright : theme.colors.status.success};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    text-shadow: ${({ $variant }) => 
      $variant === 'systems-engineering' 
        ? '0 0 4px rgba(0, 255, 127, 0.5), 1px 1px 2px rgba(0, 0, 0, 0.8)' 
        : '1px 1px 2px rgba(0, 0, 0, 0.5)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const CredibilityBanner = styled.div`
  background: 
    linear-gradient(135deg, rgba(37, 99, 235, 0.5) 0%, rgba(59, 130, 246, 0.5) 100%),
    url('/images/about/manufacturing-industry-leadership.webp');
  background-size: cover;
  background-position: center;
  color: ${({ theme }) => theme.colors.text.inverse};
  padding: ${({ theme }) => theme.spacing['2xl']};
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  text-align: center;
  box-shadow: ${({ theme }) => theme.shadows.xl};
  position: relative;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

const BannerText = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin: 0;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const AboutSection = () => {
  console.log('AboutSection updated - contrast and colors fixed');
  return (
    <AboutContainer id="about">
      <AboutContent>
        <AboutTitle>Multi-Dimensional Expertise for Complex Challenges</AboutTitle>
        <AboutSubtitle>
          Four decades of technical evolution combined with Fortune 500 results and strategic intelligence 
          for transforming manufacturing operations at global scale.
        </AboutSubtitle>

        <ExpertiseGrid>
          <ExpertiseCard variant="systems-engineering">
            <RoyalNavyLogo 
              src="/images/about/Logo_of_the_Royal_Navy.svg.png" 
              alt="Royal Navy Logo"
            />
            <CardTitle>Royal Navy Systems Engineering Foundation</CardTitle>
            <CardDescription>
              Comprehensive technical understanding spanning four decades of technological evolution—from 
              fundamental logic gates to cutting-edge AI applications. This unique perspective enables 
              strategic technology leadership that bridges operational realities with emerging competitive advantages.
            </CardDescription>
            <KeyAchievements>
              <Achievement $variant="systems-engineering">40+ years technical evolution mastery</Achievement>
              <Achievement $variant="systems-engineering">Logic gates to AI application expertise</Achievement>
              <Achievement $variant="systems-engineering">Systems engineering discipline foundation</Achievement>
            </KeyAchievements>
          </ExpertiseCard>

          <ExpertiseCard>
            <CardIcon>F5</CardIcon>
            <CardTitle>Proven Fortune 500 Track Record</CardTitle>
            <CardDescription>
              85% global reduction in warehouse accidents across Amazon, Land Rover, and Nestlé facilities 
              through complete IoT system architecture. Demonstrated ability to solve real manufacturing 
              problems at enterprise scale with quantifiable results.
            </CardDescription>
            <KeyAchievements>
              <Achievement>85% reduction warehouse accidents globally</Achievement>
              <Achievement>Amazon, Land Rover, Nestlé deployments</Achievement>
              <Achievement>Complete IoT architecture mastery</Achievement>
              <Achievement>Enterprise-scale problem solving</Achievement>
            </KeyAchievements>
          </ExpertiseCard>

          <ExpertiseCard>
            <CardIcon>BA</CardIcon>
            <CardTitle>Business Architecture Mastery</CardTitle>
            <CardDescription>
              Doubled NEC worldwide revenue through strategic technology positioning. Generated $5M+ revenue 
              with iPanel business model innovation. Proven ability to transform technical capabilities into 
              competitive market advantages.
            </CardDescription>
            <KeyAchievements>
              <Achievement>Doubled NEC worldwide revenue</Achievement>
              <Achievement>$5M+ revenue through innovation</Achievement>
              <Achievement>Technical-to-business transformation</Achievement>
              <Achievement>Long-term value creation focus</Achievement>
            </KeyAchievements>
          </ExpertiseCard>

          <ExpertiseCard>
            <CardIcon>SI</CardIcon>
            <CardTitle>Strategic Intelligence Understanding</CardTitle>
            <CardDescription>
              Deep comprehension of systemic forces driving manufacturing transformation including Industry 5.0 
              positioning, global consolidation cycles, and historical pattern recognition enabling strategic 
              rather than reactive guidance.
            </CardDescription>
            <KeyAchievements>
              <Achievement>Industry 5.0 transformation expertise</Achievement>
              <Achievement>Global consolidation pattern analysis</Achievement>
              <Achievement>Strategic foresight capabilities</Achievement>
              <Achievement>Next-decade positioning guidance</Achievement>
            </KeyAchievements>
          </ExpertiseCard>
        </ExpertiseGrid>

        <CredibilityBanner>
          <BannerText>
            Multi-dimensional expertise combining Royal Navy systems engineering, Fortune 500 enterprise scaling, 
            and cutting-edge AI implementation for manufacturing transformation that positions clients advantageously 
            for next-decade industry transitions.
          </BannerText>
        </CredibilityBanner>
      </AboutContent>
    </AboutContainer>
  );
};

export default AboutSection;